import { useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { FaExternalLinkAlt } from "react-icons/fa";
import { Link as RouterLink, useNavigate, useParams } from "react-router";

import { Flex, Heading, Input, Stack, Alert, Link as ChakraLink } from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { AsyncSelect, Select } from "chakra-react-select";
import isDate from "validator/es/lib/isDate";
import * as z from "zod";

import {
  createPostingHohishesHohishUuidPostingsPost,
  deletePostingHohishesHohishUuidPostingsPostingUuidDelete,
  updatePostingHohishesHohishUuidPostingsPostingUuidPost,
  JobType,
} from "@/codegen/openapi2";
import { Button } from "@/codegen/ui/button";
import {
  DialogActionTrigger,
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
} from "@/codegen/ui/dialog";
import { toaster } from "@/codegen/ui/toaster";
import { AsyncTerritoriesSelect, TerritoryUuidsType } from "@/components/2/fields/geo";
import { FormStepCustom } from "@/components/2/form";
import { usePermerRequirements } from "@/hooks/useHasPermission";
import { useTerritoriesPromise } from "@/services/2/geo/standalone-fetches";
import {
  getEnrichedHohishKey,
  useGetEnrichedHohishPostings,
  useGetJobs,
} from "@/services/2/hohishes/queries";
import { getJobGroups } from "@/services/2/hohishes/standalone-fetches";
import { EnrichedHohishPostingType } from "@/services/2/hohishes/types";
import { queryClient } from "@/services/2/react-query";

import { useEnrichedHohishOutletContext } from "../local-context";

const createPostingSchema = (lastDayDate: string | null, jobs: JobType[] | undefined) =>
  z
    .object({
      type: z.enum(["permanent", "backup"]),
      job: z.string(),
      starts_on: z
        .string()
        .refine((val: string) => isDate(val, { format: "YYYY-MM-DD", strictMode: true })),
      ends_on: z.preprocess(
        val => (val === "" ? null : val),
        z
          .string()
          .nullable()
          .refine(
            (val: string | null) => !val || isDate(val, { format: "YYYY-MM-DD", strictMode: true })
          )
      ),
      territories: z.object({
        agency_uuids: z.array(z.string()),
        zone_uuids: z.array(z.string()),
        region_uuids: z.array(z.string()),
      }),
    })
    .superRefine((data, ctx) => {
      if (data.ends_on && data.ends_on < data.starts_on) {
        ctx.addIssue({
          code: "custom",
          message: "Le dernier jour ne peut pas être avant le premier jour",
          path: ["ends_on"],
        });
      }
      if (lastDayDate && data.starts_on > lastDayDate) {
        ctx.addIssue({
          code: "custom",
          message: `La date ne peut pas dépasser le dernier jour (${lastDayDate})`,
          path: ["starts_on"],
        });
      }
      const jobDef = jobs?.find(j => j.value === data.job);
      if (
        data.type === "permanent" &&
        jobDef?.with_agencies &&
        data.territories.agency_uuids.length === 0 &&
        data.territories.zone_uuids.length === 0 &&
        data.territories.region_uuids.length === 0
      ) {
        ctx.addIssue({
          code: "custom",
          message: "Un territoire est requis pour ce poste",
          path: ["territories"],
        });
      }
    });

const uxWaiter = () => new Promise(resolve => setTimeout(resolve, 300));

type PostingSchemaInput = z.input<ReturnType<typeof createPostingSchema>>;
type PostingSchemaType = z.output<ReturnType<typeof createPostingSchema>>;

type GroupedJobOptionsType = {
  label: string;
  options: JobType[];
};

type PostingTypeType = {
  label: string;
  value: string;
};
const postingTypes = [
  { label: "Permanente", value: "permanent" },
  { label: "Backup", value: "backup" },
];

const getGroupedJobOptions = async (): Promise<GroupedJobOptionsType[]> => {
  const jobGroups = await getJobGroups();
  return jobGroups.map(group => ({
    label: group.label,
    options: group.jobs,
  }));
};

const CreateEditPostingHohish = ({
  existingPosting,
}: {
  existingPosting?: EnrichedHohishPostingType;
}) => {
  const hohish = useEnrichedHohishOutletContext();
  const navigate = useNavigate();
  const { data: jobs } = useGetJobs();
  const schema = createPostingSchema(hohish.last_day_date, jobs);
  const {
    register,
    handleSubmit,
    control,
    watch,
    formState: { errors, isSubmitting },
    setError,
  } = useForm<PostingSchemaInput, any, PostingSchemaType>({
    resolver: zodResolver(schema),
    defaultValues: {
      type: existingPosting?.type || "permanent",
      job: existingPosting?.job,
      starts_on: existingPosting?.starts_on,
      ends_on: existingPosting?.ends_on,
      territories: {
        agency_uuids: existingPosting?.agency_uuids || [],
        zone_uuids: existingPosting?.zone_uuids || [],
        region_uuids: existingPosting?.region_uuids || [],
      },
    },
  });
  const territoriesPromise = useTerritoriesPromise(hohish.legal_entity_uuid);
  const permer = usePermerRequirements();
  const canDeletePosting = permer.hasDsitUserPostingDelete();
  const [isDeletingDialogOpen, setIsDeletingDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const actionLabel = existingPosting ? "Modification" : "Création";

  return (
    <>
      <Stack gap={6} maxW={480}>
        <Heading size="xl">{actionLabel} d'une affectation</Heading>

        <form
          onSubmit={handleSubmit(formData => {
            const { territories, ...baseBody } = formData;
            const body = {
              ...baseBody,
              agency_uuids: territories.agency_uuids,
              zone_uuids: territories.zone_uuids,
              region_uuids: territories.region_uuids,
            };
            const path = { hohish_uuid: hohish.uuid };

            const waiter = uxWaiter();

            return (
              existingPosting
                ? updatePostingHohishesHohishUuidPostingsPostingUuidPost({
                    body,
                    path: { ...path, posting_uuid: existingPosting.uuid },
                  })
                : createPostingHohishesHohishUuidPostingsPost({ body, path })
            )
              .then(r => {
                if (r.response.status >= 400) {
                  const overlappingPostingsError = r.error?.detail?.find(
                    d => d.type === "overlapping_postings"
                  );
                  const postingAfterLastDayError = r.error?.detail?.find(
                    d => d.type === "posting_after_last_day"
                  );
                  const startsOnAfterEndsOnError = r.error?.detail?.find(
                    d => d.type === "starts_on_after_ends_on"
                  );
                  if (overlappingPostingsError) {
                    setError("root.serverOverlappingPostings", {
                      type: "serverOverlappingPostings",
                      message: overlappingPostingsError.msg,
                    });
                  } else if (postingAfterLastDayError) {
                    setError("root.serverPostingAfterLastDay", {
                      type: "serverPostingAfterLastDay",
                      message: postingAfterLastDayError.msg,
                    });
                  } else if (startsOnAfterEndsOnError) {
                    setError("root.serverStartsOnAfterEndsOn", {
                      type: "serverStartsOnAfterEndsOn",
                      message: startsOnAfterEndsOnError.msg,
                    });
                  } else if (
                    r.error?.detail?.find(d => d.type === "permanent_posting_requires_territory")
                  ) {
                    setError("root.serverTerritoryRequired", {
                      type: "serverTerritoryRequired",
                      message: "Un territoire est requis pour ce poste",
                    });
                  } else {
                    setError("root.server", {
                      type: "server",
                      message: `Server answered ${r.response.status}`,
                    });
                  }
                  return { type: "error" };
                }
                return { type: "success" };
              })
              .then(async ({ type }) => {
                if (type === "success") {
                  // Not too fast, to let the user see the loading state.
                  await waiter;

                  toaster.create({
                    title: `${actionLabel} réussie`,
                    description: "L'affectation a bien été enregistrée.",
                    type: "success",
                  });

                  queryClient.removeQueries({
                    queryKey: getEnrichedHohishKey(hohish.uuid, ["uncached"]),
                  });

                  void navigate("../postings");
                }
              });
          })}
        >
          <Stack gap={6}>
            <FormStepCustom
              label="Type"
              input={
                <Controller
                  render={({ field: { onChange, onBlur, value, ref } }) => (
                    <Select
                      options={postingTypes}
                      ref={ref}
                      onChange={(option: PostingTypeType | null) =>
                        onChange(option ? option.value : null)
                      }
                      onBlur={onBlur}
                      value={postingTypes.find(option => option.value === value) || null}
                    />
                  )}
                  name="type"
                  control={control}
                />
              }
              error={errors.type}
              helpText={
                <>
                  Un collaborateur ne peut avoir qu'une seule affectation permanente sur une période
                  donnée. Il peut en revanche assurer un ou plusieurs backups en parallèle.
                </>
              }
            />

            <FormStepCustom
              label="Job / Équipe"
              input={
                <Controller
                  render={({ field: { onChange, onBlur, value, ref } }) => (
                    <AsyncSelect
                      isClearable={true}
                      defaultOptions
                      loadOptions={async (inputValue: string) => {
                        const groupedJobOptions = await getGroupedJobOptions();
                        return groupedJobOptions.map(group => ({
                          ...group,
                          options: group.options.filter(
                            p =>
                              !inputValue || !p.label.search(new RegExp(`^.*${inputValue}.*$`, "i"))
                          ),
                        }));
                      }}
                      ref={ref}
                      onChange={(option: JobType | null) => onChange(option ? option.value : null)}
                      onBlur={onBlur}
                      value={
                        jobs && value
                          ? jobs.find((option: JobType) => option.value === value)
                          : null
                      }
                    />
                  )}
                  name="job"
                  control={control}
                />
              }
              error={errors.job}
            />

            <FormStepCustom
              label="Premier jour"
              input={
                <Input
                  type="date"
                  max={hohish.last_day_date ?? undefined}
                  {...register("starts_on")}
                />
              }
              error={errors.starts_on}
            />

            <FormStepCustom
              label="Dernier jour"
              input={
                <Input type="date" min={watch("starts_on") ?? undefined} {...register("ends_on")} />
              }
              error={errors.ends_on}
              helpText="Ce champs reste généralement vide, sauf dans le cas des affectations temporaires (eg: les backups et les collaborateurs itinérants)."
            />

            <FormStepCustom
              label="Territoire(s)"
              input={
                <Controller
                  render={({ field: { onChange, onBlur, value, ref } }) => (
                    <AsyncTerritoriesSelect
                      selectableTerritoriesPromise={territoriesPromise}
                      refCallback={ref}
                      onChange={(v: TerritoryUuidsType | null) => onChange(v)}
                      onBlur={onBlur}
                      value={value}
                    />
                  )}
                  name="territories"
                  control={control}
                />
              }
              error={errors.territories}
              helpText={
                <>
                  <b>Attention</b>, il s'agit du <b>territoire de référence</b> du collaborateur{" "}
                  <i style={{ fontStyle: "italic" }}>(parfois mais rarement "des" territoires)</i>.
                  Il ne s'agit pas des territoires auxquels le collaborateur doit avoir accès sur
                  Hassibot.
                </>
              }
            />

            {errors.root?.serverPostingAfterLastDay && (
              <Alert.Root status="error">
                <Alert.Indicator />
                <Alert.Content>
                  <Alert.Title>Date après le dernier jour</Alert.Title>
                  <Alert.Description>
                    Enregistrement impossible : les dates de l'affectation dépassent le dernier jour
                    du collaborateur ({errors.root.serverPostingAfterLastDay.message}).
                  </Alert.Description>
                </Alert.Content>
              </Alert.Root>
            )}

            {errors.root?.serverStartsOnAfterEndsOn && (
              <Alert.Root status="error">
                <Alert.Indicator />
                <Alert.Content>
                  <Alert.Title>Dates incohérentes</Alert.Title>
                  <Alert.Description>
                    Enregistrement impossible : le premier jour ne peut pas être après le dernier
                    jour de l'affectation.
                  </Alert.Description>
                </Alert.Content>
              </Alert.Root>
            )}

            {errors.root?.serverTerritoryRequired && (
              <Alert.Root status="error">
                <Alert.Indicator />
                <Alert.Content>
                  <Alert.Title>Territoire requis</Alert.Title>
                  <Alert.Description>
                    Enregistrement impossible : un territoire est requis pour les affectations
                    permanentes sur ce poste.
                  </Alert.Description>
                </Alert.Content>
              </Alert.Root>
            )}

            {errors.root?.serverOverlappingPostings && (
              <Alert.Root status="error">
                <Alert.Indicator />
                <Alert.Content>
                  <Alert.Title>Affectation en conflit</Alert.Title>
                  <Alert.Description>
                    Enregistrement impossible : les dates d'une autre affectation entrerait en
                    conflit avec celles-ci.{" "}
                    <RouterLink
                      to={`../postings/${errors.root.serverOverlappingPostings.message!}/edit`}
                      target="_blank"
                    >
                      <ChakraLink variant="underline" colorPalette={"blue"} as="span">
                        Modifiez l'autre affectation{" "}
                        <FaExternalLinkAlt style={{ display: "inline", fontSize: "75%" }} />
                      </ChakraLink>
                    </RouterLink>{" "}
                    avant de continuer ici.
                  </Alert.Description>
                </Alert.Content>
              </Alert.Root>
            )}

            <Flex justify="flex-end" gap={4}>
              <Button
                variant={"outline"}
                loading={isSubmitting || isDeleting}
                onClick={() => navigate(-1)}
              >
                Annuler
              </Button>
              {existingPosting && canDeletePosting && (
                <Button
                  loading={isSubmitting || isDeleting}
                  variant={"outline"}
                  color={"red"}
                  onClick={() => setIsDeletingDialogOpen(true)}
                >
                  Supprimer l'affectation
                </Button>
              )}
              <Button loading={isSubmitting || isDeleting} type="submit">
                {existingPosting ? "Modifier" : "Créer"} l'affectation
              </Button>
            </Flex>
          </Stack>
        </form>
      </Stack>

      <DialogRoot
        role="alertdialog"
        open={isDeletingDialogOpen}
        closeOnInteractOutside={true}
        onOpenChange={({ open }) => setIsDeletingDialogOpen(open)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Suppression de l'affectation</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <p>Vous allez supprimer l'affectation, confirmez-vous ?</p>
          </DialogBody>
          <DialogFooter>
            <DialogActionTrigger asChild>
              <Button variant="outline">Annuler</Button>
            </DialogActionTrigger>
            <Button
              colorPalette="red"
              onClick={async () => {
                setIsDeleting(true);
                const waiter = uxWaiter();
                await deletePostingHohishesHohishUuidPostingsPostingUuidDelete({
                  path: { hohish_uuid: hohish.uuid, posting_uuid: existingPosting!.uuid },
                });
                // Not too fast, to let the user see the loading state.
                await waiter;
                void navigate(-1);
              }}
            >
              Supprimer l'affectation
            </Button>
          </DialogFooter>
          <DialogCloseTrigger />
        </DialogContent>
      </DialogRoot>
    </>
  );
};

export const CreatePostingHohish = CreateEditPostingHohish;
export const EditPostingHohish = () => {
  const { postingUuid } = useParams();
  const hohish = useEnrichedHohishOutletContext();
  const { data } = useGetEnrichedHohishPostings(
    { path: { hohish_uuid: hohish.uuid } },
    ["uncached"],
    {}
  );
  if (!data) {
    return null;
  }

  const posting = data.find(p => p.uuid === postingUuid);
  if (!posting) {
    return null;
  }

  return <CreateEditPostingHohish existingPosting={posting} />;
};
